/**
 * Regras puras da recusa de "Abrir com Usuário do AD" (R2CLOUD-1259), fora do serviço Angular para
 * poderem ser testadas sem TestBed — mesmo arranjo de `stomp-alert-rules.ts`,
 * `desktop-server-removal-rules.ts` e `contingency-sync-rules.ts`.
 *
 * O defeito: o botão "Abrir com Usuário do AD" só mandava `SEND /stomp/open-ad-admin` e fechava o
 * dialog. Os quatro desfechos em que a API NÃO abre o servidor morriam do lado de lá — um deles
 * (cliente nativo desconectado) nem log nem métrica deixava. Para quem clicava, "abriu" e "não
 * abriu" eram visualmente idênticos: nada acontecia nos dois casos.
 *
 * A API passou a publicar `OpenServerErrorEvent` em `/user/topic/open-server-error`, e o payload é
 * uma ETIQUETA FECHADA de propósito: `reason` e, no máximo, `hostname`. Nunca texto de exceção —
 * quem lê o evento é o navegador de uma pessoa, e um `message` de exceção carregaria login, host ou
 * id de outro tenant sem ninguém perceber. **Logo, a frase que o usuário lê nasce aqui**, não na
 * API.
 *
 * ⚠️ O que este módulo deliberadamente NÃO faz:
 * - **Não** importa `models.ts`. É a mesma escolha dos vizinhos: `models.ts` é gerado pela API e tem
 *   `enum`, que o `--experimental-strip-types` do Node (como `npm test` roda) recusa. O acoplamento
 *   aqui é estrutural — `reason` entra como `string` e é normalizada, o que também deixa o portal
 *   sobreviver a um motivo novo que a API venha a publicar antes de o portal ser regenerado.
 * - **Não** abre diálogo nem toca em Angular. Quem renderiza é o `OpenServerErrorService`.
 */

/** Os motivos que a API publica hoje. Espelha `OpenServerErrorReason` do `models.ts`. */
export type OpenServerErrorReasonName =
    /** Não há conta de AD para esta pessoa no domínio do servidor — nem individual, nem de pool. */
    'NO_AD_USER'
    /** Existe conta, mas quem pediu não passa no portão de abertura do servidor. */
    | 'ACCESS_DENIED'
    /** O cliente nativo Windows da pessoa não está conectado: não há para quem mandar a abertura. */
    | 'NATIVE_CLIENT_OFFLINE'
    /** Qualquer outra falha do handler. O detalhe fica no log e na métrica da API, nunca no payload. */
    | 'UNEXPECTED_ERROR';

/**
 * Como o portal chama o cliente nativo Windows para o usuário final: **"Agente"**.
 *
 * Não é escolha nova — é a palavra que o `client-status-toolbar` já usa no rótulo, no tooltip
 * ("Agente conectado", "Agente desconectado") e no alerta ("Agente não detectado."). Inventar aqui
 * um "cliente nativo" ou um "aplicativo R2" faria a mesma coisa ter dois nomes na mesma tela.
 */
export const NATIVE_CLIENT_LABEL = 'Agente';

/** Motivo usado quando a API mandar algo que este portal ainda não conhece. */
const FALLBACK_REASON: OpenServerErrorReasonName = 'UNEXPECTED_ERROR';

const KNOWN_REASONS: readonly OpenServerErrorReasonName[] = [
    'NO_AD_USER',
    'ACCESS_DENIED',
    'NATIVE_CLIENT_OFFLINE',
    'UNEXPECTED_ERROR',
];

/**
 * O hostname que dá para mostrar, ou `null`.
 *
 * `hostname` é `String?` no evento e o portal não controla o que veio do banco: em vez de espalhar
 * `if` pelas mensagens, tudo que não serve para nomear um servidor — `null`, `undefined`, string
 * vazia, só espaços — vira `null` num lugar só. Sem isso a frase sairia como "o servidor <b></b>".
 */
export function openServerErrorHostname(hostname: string | null | undefined): string | null {
    const trimmed = (hostname ?? '').trim();
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Escapa o que vai ser interpolado no HTML da mensagem.
 *
 * O `hostname` é dado de banco (`desktop_server.hostname`, escrito por quem cadastra servidor) e a
 * frase é renderizada por `[innerHTML]`. O sanitizador do Angular já barra script — o diálogo não usa
 * `bypassSecurityTrustHtml` —, mas ele **permite** marcação inócua como link e formatação, e um
 * hostname com `<a href>` viraria isca de phishing dentro de um modal legítimo do portal. Aqui o
 * hostname é conteúdo, nunca marcação: escapar é o que diz isso no código.
 */
function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Motivo conhecido a partir do que veio no evento.
 *
 * Motivo desconhecido, ausente ou nulo cai em `UNEXPECTED_ERROR` — que é justamente a frase genérica
 * "não deu, tente de novo e acione o suporte". O erro seguro aqui é dizer algo verdadeiro e vago, e
 * nunca deixar a tela muda de novo, que é o defeito que este ticket veio consertar.
 */
export function normalizeOpenServerErrorReason(reason: string | null | undefined): OpenServerErrorReasonName {
    const candidate = (reason ?? '').trim();
    return KNOWN_REASONS.find((known) => known === candidate) ?? FALLBACK_REASON;
}

/**
 * A frase que o usuário lê, em português, para um motivo e (quando vier) um servidor.
 *
 * Duas versões por motivo, e não um "servidor {host}" com host vazio: quando a API não sabe qual
 * servidor era — o `desktopServerId` pode nem ter chegado ao ponto de virar entidade —, a frase se
 * refere ao servidor sem tentar nomeá-lo. O `<b>` é intencional: o diálogo renderiza a mensagem por
 * `[innerHTML]` (ver `confirm-dialog.component.html`), como as outras mensagens do portal.
 */
export function openServerErrorMessage(reason: string | null | undefined, hostname?: string | null): string {
    const hostnameToShow = openServerErrorHostname(hostname);
    const server = hostnameToShow === null ? null : escapeHtml(hostnameToShow);
    switch (normalizeOpenServerErrorReason(reason)) {
        case 'NO_AD_USER':
            return server
                ? `Você não tem um usuário do Active Directory no domínio do servidor <b>${server}</b>. `
                + 'Peça ao administrador para liberar o seu acesso.'
                : 'Você não tem um usuário do Active Directory no domínio desse servidor. '
                + 'Peça ao administrador para liberar o seu acesso.';
        case 'ACCESS_DENIED':
            return server
                ? `Você não tem permissão para abrir o servidor <b>${server}</b> com usuário do Active Directory. `
                + 'Fale com o administrador.'
                : 'Você não tem permissão para abrir esse servidor com usuário do Active Directory. '
                + 'Fale com o administrador.';
        case 'NATIVE_CLIENT_OFFLINE':
            return server
                ? `O ${NATIVE_CLIENT_LABEL} não está conectado neste computador, então o servidor <b>${server}</b> não pôde ser aberto. `
                + `Abra o ${NATIVE_CLIENT_LABEL} e tente de novo.`
                : `O ${NATIVE_CLIENT_LABEL} não está conectado neste computador, então o servidor não pôde ser aberto. `
                + `Abra o ${NATIVE_CLIENT_LABEL} e tente de novo.`;
        case 'UNEXPECTED_ERROR':
            return server
                ? `Não foi possível abrir o servidor <b>${server}</b>. `
                + 'Tente novamente e, se continuar, acione o suporte.'
                : 'Não foi possível abrir o servidor. '
                + 'Tente novamente e, se continuar, acione o suporte.';
    }
}
